import type { GTooltipCardData } from '~/presentation/ui';

import { IPAMetricKey } from '../types/chart-tooltip';
import { useTranslation } from 'react-i18next';
import { EAnalyticMetricKey } from '../types';
import { useCallback, useMemo } from 'react';

const PA_METRIC_TO_ANALYTIC_MAP: Partial<Record<IPAMetricKey, EAnalyticMetricKey>> = {
  [IPAMetricKey.PAGE_AVG_TIME]: EAnalyticMetricKey.AVG_TIME,
  [IPAMetricKey.PAGE_SESSIONS]: EAnalyticMetricKey.SESSIONS,
  [IPAMetricKey.PAGE_VIEWS]: EAnalyticMetricKey.PAGE_VIEWS,
  [IPAMetricKey.PAGE_ORDERS]: EAnalyticMetricKey.ORDERS,
  [IPAMetricKey.PAGE_REVENUE]: EAnalyticMetricKey.REVENUE,
};

type IAnalyticMetricTooltipKeyValue = Record<EAnalyticMetricKey, GTooltipCardData>;

interface IAnalyticMetricTooltipProps {
  width?: GTooltipCardData['width'];
}
interface IAnalyticMetricTooltipResult {
  baseTooltip: IAnalyticMetricTooltipKeyValue;
  getPageAnalyticTooltip: (key: IPAMetricKey) => GTooltipCardData | undefined;
  getAnalyticTooltip: (key: EAnalyticMetricKey) => GTooltipCardData | undefined;
}

export const useAnalyticMetricTooltip = (
  props?: IAnalyticMetricTooltipProps,
): IAnalyticMetricTooltipResult => {
  const { t } = useTranslation();
  const { width = 'default' } = props ?? {};

  const baseTooltip: IAnalyticMetricTooltipKeyValue = useMemo(
    () => ({
      [EAnalyticMetricKey.ORDERS]: {
        title: t('Orders'),
        content: t('Number of orders that went through this pages'),
        width,
      },
      [EAnalyticMetricKey.SESSIONS]: {
        title: t('Sessions'),
        content: t('A period during which a visitor interacts with your online store'),
        width,
      },
      [EAnalyticMetricKey.ENGAGED_SESSIONS]: {
        title: t('Engaged sessions'),
        content: t(
          'A period during which a visitor interacts with your online store, only from selected device.',
        ),
        width,
      },
      [EAnalyticMetricKey.ENGAGED_SESSIONS_DEVICE]: {
        title: t('Engaged sessions by device'),
        content: t(
          'A period during which a visitor interacts with your online store, only from selected device.',
        ),
        width,
      },
      [EAnalyticMetricKey.PAGE_VIEWS]: {
        title: t('Pageviews'),
        content: t('Number of times a page on your online store has been viewed by visitors'),
        width,
      },
      [EAnalyticMetricKey.REVENUE]: {
        title: t('Revenue'),
        content: t('Total income generated from sales after discounts'),
        formula: (
          <>
            <span className="formula-variable">{t('Revenue')}</span>
            <span> = </span>
            <span className="formula-input">{t('gross sales')}</span>
            <span className="formula-operator"> - </span>
            <span className="formula-input">{t('discounts')}</span>
          </>
        ),
        width,
      },
      [EAnalyticMetricKey.AVG_TIME]: {
        title: t('Average time on page'),
        content: t(
          'Average duration that visitors spend on a page before clicking to open new page',
        ),
        formula: (
          <>
            <span className="formula-variable">{t('Average time on page')}</span>
            <span> = </span>
            <span className="formula-input">{t('total time on page')}</span>
            <span className="formula-operator"> / </span>
            <span className="formula-input">{t('(total pageviews - total exits)')}</span>
          </>
        ),
        width,
      },
      [EAnalyticMetricKey.CLICKS]: {
        title: t('Total clicks'),
        content: t('Number of clicks on this page'),
        width,
      },
      [EAnalyticMetricKey.AVG_SCROLL_DEPTH]: {
        title: t('Avg scroll depth'),
        content: t('How far your visitors scroll'),
        width,
      },
    }),
    [t, width],
  );

  const getPageAnalyticTooltip = useCallback(
    (key: IPAMetricKey): GTooltipCardData | undefined => {
      const analyticKey = PA_METRIC_TO_ANALYTIC_MAP[key];
      if (!analyticKey) return undefined;
      return baseTooltip[analyticKey];
    },
    [baseTooltip],
  );

  const getAnalyticTooltip = useCallback(
    (key: EAnalyticMetricKey): GTooltipCardData | undefined => {
      return baseTooltip[key];
    },
    [baseTooltip],
  );

  return { baseTooltip, getPageAnalyticTooltip, getAnalyticTooltip };
};
